// Shared field-status logic for Kodakku's annotation surfaces (AnnotatedFieldsSummary,
// Improve Ranking's gain computation) — single source of truth for what "still needs a human"
// means for a scorecard field, so the two surfaces can never disagree about it.

import { AUTO_VALIDATE_THRESHOLD, SCORECARD, type ScorecardKey } from "@shared/scorecard";
import type { AnnotationMap, ExtractedScorecardWithEvidence } from "./corpusApi";

export type FieldStatus = "human_reviewed" | "auto_validated" | "needs_review" | "empty";

/** The value actually in effect for a field: a human decision always wins over the AI's. */
export function effectiveLabels(
  key: ScorecardKey,
  scorecard: ExtractedScorecardWithEvidence | null,
  annotation: AnnotationMap | null
): string[] {
  return annotation?.[key]?.labels ?? scorecard?.[key]?.labels ?? [];
}

/**
 * Where a field stands: a human decision (even a deliberate "mark empty") is always final —
 * "empty" and "human_reviewed" both mean "don't ask again." Absent a human decision, a field
 * extracted above its tier's AUTO_VALIDATE_THRESHOLD is "auto_validated" (shown pre-approved,
 * not queued for review); anything else — including a genuinely low-confidence AI label, not
 * just an empty one — is "needs_review".
 */
export function fieldStatus(
  key: ScorecardKey,
  scorecard: ExtractedScorecardWithEvidence | null,
  annotation: AnnotationMap | null
): FieldStatus {
  const humanEntry = annotation?.[key];
  if (humanEntry) return humanEntry.labels.length > 0 ? "human_reviewed" : "empty";

  const preAnnotation = scorecard?.[key];
  if (!preAnnotation || preAnnotation.labels.length === 0) return "needs_review";

  const cleared = (preAnnotation.confidence ?? 0) >= AUTO_VALIDATE_THRESHOLD[SCORECARD[key].tier];
  return cleared ? "auto_validated" : "needs_review";
}
